import React, { useEffect, useMemo, useState } from "react";
import { CreditCard, TrendingUp, Clock, DollarSign, Download } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const STATUS_PILL = {
  paid: "bg-[#CFF048] text-black",
  pending: "bg-[#27272A] text-[#A1A1AA]",
  failed: "bg-red-950 text-red-400",
};

function fmt(n) { return `₹${Number(n || 0).toFixed(0)}`; }
function fmtDate(d) { return d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"; }

export default function AdminPayments() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | paid | pending

  useEffect(() => {
    api.get("/admin/payments")
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => o.payment_status === filter);
  }, [orders, filter]);

  const stats = useMemo(() => {
    const paid = orders.filter((o) => o.payment_status === "paid");
    const pending = orders.filter((o) => o.payment_status !== "paid");
    const today = paid.filter((o) => {
      const d = new Date(o.payment_date || o.created_at);
      const now = new Date();
      return d.toDateString() === now.toDateString();
    });
    return {
      total_revenue: paid.reduce((s, o) => s + (o.total || 0), 0),
      paid_count: paid.length,
      pending_amount: pending.reduce((s, o) => s + (o.total || 0), 0),
      today_revenue: today.reduce((s, o) => s + (o.total || 0), 0),
    };
  }, [orders]);

  return (
    <AdminLayout title="Payments">
      {/* STAT CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="payment-stats-grid">
        {[
          { label: "TOTAL REVENUE", value: fmt(stats.total_revenue), icon: TrendingUp, note: `${stats.paid_count} paid` },
          { label: "TODAY'S REVENUE", value: fmt(stats.today_revenue), icon: DollarSign, note: "24h window" },
          { label: "PENDING AMOUNT", value: fmt(stats.pending_amount), icon: Clock, note: "awaiting payment" },
          { label: "ALL TRANSACTIONS", value: orders.length, icon: CreditCard, note: "total orders" },
        ].map((c) => (
          <div key={c.label} className="bg-[#0A0A0A] border border-[#27272A] p-5" data-testid={`payment-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex justify-between items-start">
              <p className="label-mono text-[#71717A] text-[10px]">{c.label}</p>
              <c.icon size={14} className="text-[#CFF048]" />
            </div>
            <p className="font-display font-black text-3xl mt-3 text-white">{c.value}</p>
            <p className="label-mono text-[#52525B] text-[10px] mt-1">{c.note}</p>
          </div>
        ))}
      </div>

      {/* FILTER TABS */}
      <div className="flex gap-2 mb-4">
        {["all", "paid", "pending"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`label-mono px-4 py-2 border transition-colors ${filter === f ? "bg-[#CFF048] text-black border-[#CFF048]" : "border-[#27272A] text-[#71717A] hover:border-white"}`}
            data-testid={`payment-filter-${f}`}
          >
            {f.toUpperCase()} {f === "all" ? `(${orders.length})` : f === "paid" ? `(${orders.filter(o=>o.payment_status==="paid").length})` : `(${orders.filter(o=>o.payment_status!=="paid").length})`}
          </button>
        ))}
      </div>

      {/* PAYMENT TABLE */}
      <div className="bg-[#0A0A0A] border border-[#27272A] overflow-x-auto" data-testid="admin-payments-table">
        <table className="w-full text-sm">
          <thead className="border-b border-[#27272A]">
            <tr className="label-mono text-[#71717A] text-[10px]">
              <th className="text-left p-4">ORDER ID</th>
              <th className="text-left p-4">CUSTOMER</th>
              <th className="text-left p-4">PRODUCTS</th>
              <th className="text-right p-4">SUBTOTAL</th>
              <th className="text-right p-4">DISCOUNT</th>
              <th className="text-right p-4">TOTAL PAID</th>
              <th className="text-left p-4">STATUS</th>
              <th className="text-left p-4">PAYMENT REF</th>
              <th className="text-left p-4">DATE</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="p-8 text-center label-mono text-[#71717A]">// LOADING...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center label-mono text-[#71717A]">// NO PAYMENTS</td></tr>
            )}
            {filtered.map((o) => {
              const productNames = (o.items || []).map((it) => it.name).filter(Boolean).join(", ");
              const sessionShort = o.stripe_session_id ? o.stripe_session_id.replace("cs_", "").slice(0, 14) + "…" : "—";
              return (
                <tr key={o.id} className="border-b border-[#111] hover:bg-[#111]" data-testid={`payment-row-${o.id}`}>
                  <td className="p-4">
                    <span className="font-mono-cult text-[#CFF048]">{o.id.slice(0, 8).toUpperCase()}</span>
                  </td>
                  <td className="p-4">
                    <div className="font-display font-bold text-xs">{o.user_name || "—"}</div>
                    <div className="label-mono text-[#71717A] text-[10px] truncate max-w-[140px]">{o.user_email}</div>
                  </td>
                  <td className="p-4 text-xs text-[#A1A1AA] max-w-[160px] truncate">{productNames || "—"}</td>
                  <td className="p-4 text-right font-mono-cult text-[#A1A1AA]">{fmt(o.subtotal)}</td>
                  <td className="p-4 text-right font-mono-cult text-[#71717A]">
                    {(o.discount_amount || 0) > 0 ? (
                      <span className="text-[#CFF048]">− {fmt(o.discount_amount)}</span>
                    ) : "—"}
                  </td>
                  <td className="p-4 text-right font-mono-cult font-bold text-white">{fmt(o.total)}</td>
                  <td className="p-4">
                    <span className={`label-mono text-[10px] px-2 py-1 ${STATUS_PILL[o.payment_status] || STATUS_PILL.pending}`}>
                      {(o.payment_status || "PENDING").toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4 font-mono-cult text-[#71717A] text-[11px]" title={o.stripe_session_id || ""}>
                    {sessionShort}
                  </td>
                  <td className="p-4 font-mono-cult text-[#71717A] text-[11px] whitespace-nowrap">
                    {fmtDate(o.payment_date || o.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="label-mono text-[#52525B] text-[10px] mt-4">// PAYMENTS PROCESSED VIA STRIPE · INBOUND TO YOUR STRIPE DASHBOARD</p>
    </AdminLayout>
  );
}
